import { useEffect, useRef } from 'react';
import { useWikiSearch } from './WikiSearchContext';

const useHighlightMatches = (contentRef) => {
  const {
    searchTerm,
    currentMatchIndex,
    setTotalMatches,
    setCurrentMatchIndex,
  } = useWikiSearch();
  const allHighlightsRef = useRef([]);

  // Effect 1: Create/clear highlights when search term changes
  useEffect(() => {
    if (!contentRef.current) {
      console.log('useHighlightMatches: contentRef.current is null');
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!contentRef.current) return;

      // Clear previous highlights
      const existingHighlights = contentRef.current.querySelectorAll(
        'mark.wiki-search-highlight',
      );
      existingHighlights.forEach((highlight) => {
        const parent = highlight.parentNode;
        parent.replaceChild(
          document.createTextNode(highlight.textContent),
          highlight,
        );
        parent.normalize();
      });

      allHighlightsRef.current = [];

      if (!searchTerm || searchTerm.length < 2) {
        console.log('Search term too short or empty:', searchTerm);
        setTotalMatches(0);
        setCurrentMatchIndex(0);
        return;
      }

      console.log('Starting search for:', searchTerm);

      // Find and highlight all matches
      const walker = document.createTreeWalker(
        contentRef.current,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (
              node.parentElement?.classList?.contains('wiki-search-highlight')
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            if (
              ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(
                node.parentElement?.tagName,
              )
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );

      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.toLowerCase().includes(searchTerm.toLowerCase())) {
          textNodes.push(node);
        }
      }

      console.log('Text nodes found:', textNodes.length);

      let matchIndex = 0;
      const allHighlights = [];

      textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedTerm})`, 'gi');
        const parts = text.split(regex);

        if (parts.length === 1) return;

        const fragment = document.createDocumentFragment();

        parts.forEach((part) => {
          if (part.toLowerCase() === searchTerm.toLowerCase()) {
            const mark = document.createElement('mark');
            mark.className = 'wiki-search-highlight';
            mark.textContent = part;
            mark.setAttribute('data-match-index', matchIndex);
            mark.style.padding = '0 2px';
            mark.style.borderRadius = '2px';
            fragment.appendChild(mark);
            allHighlights.push(mark);
            matchIndex++;
          } else if (part) {
            fragment.appendChild(document.createTextNode(part));
          }
        });

        textNode.parentNode.replaceChild(fragment, textNode);
      });

      console.log('Total matches found:', matchIndex);
      allHighlightsRef.current = allHighlights;
      setTotalMatches(matchIndex);
      setCurrentMatchIndex(0);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, contentRef, setTotalMatches, setCurrentMatchIndex]);

  // Effect 2: Update styling and scroll when navigating between matches
  useEffect(() => {
    const allHighlights = allHighlightsRef.current;
    if (allHighlights.length === 0) return;

    console.log('Navigating to match:', currentMatchIndex);

    // Update all highlight styles
    allHighlights.forEach((highlight, index) => {
      highlight.style.backgroundColor =
        index === currentMatchIndex ? '#ff9800' : '#ffeb3b';
      highlight.style.fontWeight =
        index === currentMatchIndex ? 'bold' : 'normal';
    });

    // Scroll to current match
    if (allHighlights[currentMatchIndex]) {
      allHighlights[currentMatchIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentMatchIndex]);
};

export default useHighlightMatches;
